// Product Reviews custom element
// - Pagination via data-initial-count + data-page-size
// - Clear hierarchy: title dominant, body readable, author secondary
// - Inline verified badge + initials avatar
// - Clickable thumbnails open lightbox
// - No count indicators (no "Showing X of Y", no "(N)" suffix on button)

if (!customElements.get('product-reviews')) {

  /* ---------------- Lightbox singleton ---------------- */
  const FBPRLightbox = (function () {
    let el = null, imgEl = null, counterEl = null, prevBtn = null, nextBtn = null;
    let images = [], index = 0, touchStartX = 0, touchStartY = 0;

    function ensure() {
      if (el) return el;
      el = document.createElement('div');
      el.className = 'fb-pr-lightbox';
      el.setAttribute('role', 'dialog');
      el.setAttribute('aria-modal', 'true');
      el.setAttribute('aria-label', 'Review image viewer');
      el.innerHTML = `
        <button type="button" class="fb-pr-lightbox__close" aria-label="Close"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg></button>
        <button type="button" class="fb-pr-lightbox__nav fb-pr-lightbox__nav--prev" aria-label="Previous image"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg></button>
        <button type="button" class="fb-pr-lightbox__nav fb-pr-lightbox__nav--next" aria-label="Next image"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg></button>
        <div class="fb-pr-lightbox__stage"><img class="fb-pr-lightbox__img" alt=""></div>
        <span class="fb-pr-lightbox__counter"></span>
      `;
      document.body.appendChild(el);
      imgEl = el.querySelector('.fb-pr-lightbox__img');
      counterEl = el.querySelector('.fb-pr-lightbox__counter');
      prevBtn = el.querySelector('.fb-pr-lightbox__nav--prev');
      nextBtn = el.querySelector('.fb-pr-lightbox__nav--next');
      el.querySelector('.fb-pr-lightbox__close').addEventListener('click', close);
      prevBtn.addEventListener('click', () => navigate(-1));
      nextBtn.addEventListener('click', () => navigate(1));
      el.addEventListener('click', (e) => {
        if (e.target === el || e.target.classList.contains('fb-pr-lightbox__stage')) close();
      });
      document.addEventListener('keydown', (e) => {
        if (!el.classList.contains('is-open')) return;
        if (e.key === 'Escape') { e.preventDefault(); close(); }
        else if (e.key === 'ArrowLeft') { e.preventDefault(); navigate(-1); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); navigate(1); }
      });
      el.addEventListener('touchstart', (e) => {
        if (!e.touches[0]) return;
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
      }, { passive: true });
      el.addEventListener('touchend', (e) => {
        if (!e.changedTouches[0]) return;
        const dx = e.changedTouches[0].clientX - touchStartX;
        const dy = e.changedTouches[0].clientY - touchStartY;
        if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 50) navigate(dx < 0 ? 1 : -1);
      }, { passive: true });
      return el;
    }

    function update() {
      if (!imgEl || images.length === 0) return;
      el.classList.add('is-loading');
      imgEl.onload = () => el.classList.remove('is-loading');
      imgEl.onerror = () => el.classList.remove('is-loading');
      imgEl.src = images[index];
      const multi = images.length > 1;
      prevBtn.hidden = !multi;
      nextBtn.hidden = !multi;
      counterEl.hidden = !multi;
      counterEl.textContent = (index + 1) + ' / ' + images.length;
    }

    function open(imgs, startIdx) {
      ensure();
      images = imgs || [];
      index = startIdx || 0;
      if (images.length === 0) return;
      update();
      el.classList.add('is-open');
      document.body.classList.add('fb-pr-lightbox-open');
    }

    function close() {
      if (!el) return;
      el.classList.remove('is-open');
      document.body.classList.remove('fb-pr-lightbox-open');
      setTimeout(() => { if (el && !el.classList.contains('is-open') && imgEl) imgEl.src = ''; }, 300);
    }

    function navigate(dir) {
      if (images.length < 2) return;
      index = (index + dir + images.length) % images.length;
      update();
    }

    return { open: open, close: close };
  })();

  /* ---------------- Custom element ---------------- */
  class ProductReviews extends HTMLElement {
    constructor() {
      super();
      this.reviewsList = this.querySelector('.product-reviews__list');
      this.loadMoreButton = this.querySelector('.product-reviews__load-more');
      this.loadMoreLabel = this.querySelector('.product-reviews__load-more-label');
      this.loadingElement = this.querySelector('.product-reviews__loading');
      this.productId = this.dataset.productId || '';
      this.locale = this.dataset.locale || (window.Shopify && Shopify.locale) || 'en';

      this.initialCount = parseInt(this.dataset.initialCount, 10) || 6;
      this.pageSize = parseInt(this.dataset.pageSize, 10) || 6;
      this.showVerified = this.dataset.showVerified !== 'false';
      this.verifiedLabel = this.dataset.verifiedLabel || 'Verified';
      this.showMoreLabelText = this.dataset.showMoreLabel || 'Show more reviews';

      this.currentRevealed = 0;
      this.allReviews = [];
      this.totalReviews = 0;

      if (this.loadMoreButton) {
        this.loadMoreButton.addEventListener('click', this.loadMoreReviews.bind(this));
      }

      if (this.reviewsList) {
        this.reviewsList.addEventListener('click', (e) => {
          const btn = e.target.closest && e.target.closest('[data-fbpr-image-idx]');
          if (!btn) return;
          e.preventDefault();
          const container = btn.closest('[data-fbpr-images]');
          if (!container) return;
          let images = [];
          try { images = JSON.parse(container.getAttribute('data-fbpr-images') || '[]'); } catch (e) {}
          const idx = parseInt(btn.getAttribute('data-fbpr-image-idx'), 10) || 0;
          FBPRLightbox.open(images, idx);
        });
      }

      this.init();
    }

    async init() {
      try {
        await this.loadReviews();
        this.renderReviews();
        this.updateLoadMoreButton();
      } catch (error) {
        console.error('[product-reviews] init error', error);
        this.showEmptyState();
      }
    }

    async loadReviews() {
      if (!this.productId) { this.showEmptyState(); return; }
      const currentLocale = (this.locale || 'en').toUpperCase();
      const urlsElement = document.getElementById('product-reviews-urls');
      let urls = {};
      if (urlsElement) { try { urls = JSON.parse(urlsElement.textContent); } catch (e) {} }
      const localeKey = currentLocale.toLowerCase();

      try {
        const r = await fetch(`/assets/${currentLocale}_${this.productId}.csv`);
        if (r.ok) { this.parseReviews(await r.text()); return; }
      } catch (e) {}
      if (urls[localeKey]) {
        try { const r = await fetch(urls[localeKey]); if (r.ok) { this.parseReviews(await r.text()); return; } } catch (e) {}
      }
      if (currentLocale !== 'EN') {
        try { const r = await fetch(`/assets/EN_${this.productId}.csv`); if (r.ok) { this.parseReviews(await r.text()); return; } } catch (e) {}
        if (urls['en']) { try { const r = await fetch(urls['en']); if (r.ok) { this.parseReviews(await r.text()); return; } } catch (e) {} }
      }
      if (urls['legacy']) { try { const r = await fetch(urls['legacy']); if (r.ok) { this.parseReviews(await r.text()); return; } } catch (e) {} }
      this.showEmptyState();
    }

    parseReviews(csvText) {
      const lines = csvText.split('\n');
      if (lines.length === 0) return;
      const headers = this.parseCSVLine(lines[0]);
      this.allReviews = [];
      const joinedLines = [];
      let currentLine = '';
      let inQuotes = false;
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        if (!line.trim() && currentLine === '') continue;
        for (let j = 0; j < line.length; j++) {
          if (line[j] === '"' && (j === 0 || line[j-1] !== '\\')) inQuotes = !inQuotes;
        }
        currentLine = currentLine === '' ? line : currentLine + '\n' + line;
        if (!inQuotes && currentLine.trim()) { joinedLines.push(currentLine); currentLine = ''; }
      }
      if (currentLine.trim()) joinedLines.push(currentLine);

      for (let i = 1; i < joinedLines.length; i++) {
        if (!joinedLines[i].trim()) continue;
        try {
          const values = this.parseCSVLine(joinedLines[i]);
          const review = {};
          headers.forEach((header, index) => {
            let value = values[index];
            if (typeof value === 'string') {
              if (header === 'body') value = value.replace(/\n{3,}/g, '\n\n').trim();
              else value = value.replace(/\n+/g, ' ').trim();
            }
            review[header] = value;
          });
          if (review.state && (review.state.toLowerCase() === 'published' || review.state.toLowerCase() === 'unpublished')) {
            this.allReviews.push(review);
          }
        } catch (e) {}
      }
      this.totalReviews = this.allReviews.length;
    }

    parseCSVLine(line) {
      const result = [];
      let current = '';
      let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (char === '"') {
          if (inQuotes && i + 1 < line.length && line[i + 1] === '"') { current += '"'; i++; }
          else inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) { result.push(this.cleanQuotes(current)); current = ''; }
        else current += char;
      }
      result.push(this.cleanQuotes(current));
      return result;
    }

    cleanQuotes(str) {
      if (str.startsWith('"') && str.endsWith('"')) str = str.substring(1, str.length - 1);
      return str.replace(/""/g, '"');
    }

    renderReviews() {
      if (this.loadingElement) this.loadingElement.style.display = 'none';
      if (this.allReviews.length === 0) { this.showEmptyState(); return; }
      const renderCount = Math.min(this.initialCount, this.allReviews.length);
      this.reviewsList.querySelectorAll('.product-reviews__item').forEach(el => el.remove());
      for (let i = 0; i < renderCount; i++) {
        this.reviewsList.appendChild(this.createReviewElement(this.allReviews[i], i));
      }
      this.currentRevealed = renderCount;
    }

    loadMoreReviews() {
      const nextEnd = Math.min(this.currentRevealed + this.pageSize, this.allReviews.length);
      for (let i = this.currentRevealed; i < nextEnd; i++) {
        const node = this.createReviewElement(this.allReviews[i], i);
        node.classList.add('is-new');
        this.reviewsList.appendChild(node);
        requestAnimationFrame(() => requestAnimationFrame(() => node.classList.remove('is-new')));
      }
      this.currentRevealed = nextEnd;
      this.updateLoadMoreButton();
    }

    updateLoadMoreButton() {
      if (!this.loadMoreButton) return;
      if (this.currentRevealed < this.totalReviews) {
        if (this.loadMoreLabel) this.loadMoreLabel.textContent = this.showMoreLabelText;
        this.loadMoreButton.style.display = 'inline-flex';
      } else {
        this.loadMoreButton.style.display = 'none';
      }
    }

    getInitials(name) {
      if (!name) return '?';
      const parts = name.trim().split(/\s+/).filter(Boolean);
      if (parts.length === 0) return '?';
      if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    }

    getAvatarColor(name) {
      const palette = ['#1d5b91', '#0f3a5f', '#1f6f47', '#a85b1f', '#7a3e8f', '#2a6c7a', '#b14e4e', '#5a6f3a'];
      let hash = 0;
      for (let i = 0; i < (name || '').length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
      return palette[Math.abs(hash) % palette.length];
    }

    escapeHtml(s) {
      if (s == null) return '';
      return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
    }

    createReviewElement(review, index) {
      const reviewElement = document.createElement('div');
      reviewElement.classList.add('product-reviews__item');

      let imageUrls = [];
      if (review.imageUrl) imageUrls = review.imageUrl.split(',').map(u => u.trim()).filter(Boolean);

      let cleanTitle = '';
      if (review.title) {
        const parts = review.title.split('\n').filter(p => p.trim());
        cleanTitle = parts.length > 0 ? parts[parts.length - 1].trim() : review.title;
      }

      const author = review.author || 'Anonymous';
      const initials = this.getInitials(author);
      const avatarColor = this.getAvatarColor(author);

      let isVerified = this.showVerified;
      if (review.verified_purchase !== undefined && review.verified_purchase !== null && review.verified_purchase !== '') {
        const v = String(review.verified_purchase).toLowerCase().trim();
        isVerified = (v === 'true' || v === '1' || v === 'yes' || v === 'verified') && this.showVerified;
      }

      const verifiedBadge = isVerified ? `
        <span class="product-reviews__verified" title="${this.escapeHtml(this.verifiedLabel)} buyer">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"></polyline></svg>
          ${this.escapeHtml(this.verifiedLabel)}
        </span>` : '';

      const ratingHtml = this.createStarRating(parseFloat(review.rating));

      let imagesHtml = '';
      if (imageUrls.length > 0) {
        const imagesJson = this.escapeHtml(JSON.stringify(imageUrls));
        imagesHtml = `
          <div class="product-reviews__images" data-fbpr-images="${imagesJson}">
            ${imageUrls.map((url, idx) => `
              <button type="button" class="product-reviews__image-container" data-fbpr-image-idx="${idx}" aria-label="View review image ${idx + 1} of ${imageUrls.length} full size">
                <img src="${this.escapeHtml(url)}" alt="Review image" class="product-reviews__image" loading="lazy">
                <span class="zoom-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line><line x1="11" y1="8" x2="11" y2="14"></line><line x1="8" y1="11" x2="14" y2="11"></line></svg></span>
              </button>
            `).join('')}
          </div>
        `;
      }

      reviewElement.innerHTML = `
        <div class="product-reviews__head">
          <span class="product-reviews__avatar" style="background:${avatarColor};" aria-hidden="true">${this.escapeHtml(initials)}</span>
          <div class="product-reviews__head-body">
            <div class="product-reviews__author-row">
              <span class="product-reviews__author-name">${this.escapeHtml(author)}</span>
              ${verifiedBadge}
            </div>
            <div class="product-reviews__rating">${ratingHtml}</div>
          </div>
        </div>
        ${cleanTitle ? `<h4 class="product-reviews__item-title">${this.escapeHtml(cleanTitle)}</h4>` : ''}
        <div class="product-reviews__content">${this.escapeHtml(review.body || '').replace(/\n/g, '<br>')}</div>
        ${imagesHtml}
      `;
      return reviewElement;
    }

    createStarRating(rating) {
      if (isNaN(rating)) rating = 5;
      const fullStars = Math.floor(rating);
      const hasHalfStar = (rating % 1) >= 0.5;
      const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
      const full = '<span class="product-reviews__star product-reviews__star--full" aria-hidden="true">★</span>';
      const empty = '<span class="product-reviews__star product-reviews__star--empty" aria-hidden="true">☆</span>';
      const half = '<span class="product-reviews__star product-reviews__star--half" aria-hidden="true">★</span>';
      return `<div class="product-reviews__stars" role="img" aria-label="${rating} out of 5 stars">${full.repeat(fullStars)}${hasHalfStar ? half : ''}${empty.repeat(emptyStars)}</div>`;
    }

    showEmptyState() {
      if (this.loadingElement) this.loadingElement.style.display = 'none';
      if (this.reviewsList) {
        const emptyElement = document.createElement('div');
        emptyElement.classList.add('product-reviews__empty');
        emptyElement.textContent = 'No reviews yet for this product';
        this.reviewsList.innerHTML = '';
        this.reviewsList.appendChild(emptyElement);
      }
      if (this.loadMoreButton) this.loadMoreButton.style.display = 'none';
    }
  }

  customElements.define('product-reviews', ProductReviews);

  const ensureChildren = (node) => {
    if (node.querySelector('.product-reviews__list')) return;
    const listElement = document.createElement('div');
    listElement.classList.add('product-reviews__list');
    const loadingElement = document.createElement('div');
    loadingElement.classList.add('product-reviews__loading');
    loadingElement.innerHTML = '<div class="loading-overlay__spinner"><svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg"><circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle></svg></div>';
    listElement.appendChild(loadingElement);
    const actionsElement = document.createElement('div');
    actionsElement.classList.add('product-reviews__actions');
    actionsElement.innerHTML = '<button type="button" class="product-reviews__load-more" style="display:none;"><span class="product-reviews__load-more-label">Show more reviews</span><svg class="product-reviews__load-more-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg></button>';
    node.appendChild(listElement);
    node.appendChild(actionsElement);
  };

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        if (node.nodeName.toLowerCase() === 'product-reviews' && !node._initialized) {
          ensureChildren(node);
          node._initialized = true;
          new ProductReviews(node);
        }
        if (node.querySelectorAll) {
          node.querySelectorAll('product-reviews').forEach((el) => {
            if (!el._initialized) { ensureChildren(el); el._initialized = true; new ProductReviews(el); }
          });
        }
      });
    });
  });
  observer.observe(document.body, { childList: true, subtree: true });

  const initExisting = () => {
    document.querySelectorAll('product-reviews').forEach((el) => {
      if (!el._initialized) { ensureChildren(el); el._initialized = true; new ProductReviews(el); }
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initExisting);
  else initExisting();
}
